import SESSION_CONFIG from './session-config';

class SessionManager {
  constructor(auth) {
    this.auth = auth;
    // Timing values are read fresh in startSession() so a server-config overlay
    // (loadServerConfig) applied before start takes effect (#628).
    this.idleTimeout = SESSION_CONFIG.IDLE_TIMEOUT;
    this.slidingWindow = SESSION_CONFIG.SLIDING_WINDOW;
    this.warningTime = SESSION_CONFIG.WARNING_TIME;

    this.idleTimer = null;
    this.warningTimer = null;
    this.tokenRefreshTimer = null;
    this.slidingWindowWarningTimer = null;
    this.slidingWindowTimer = null;
    this.lastActivityTime = null;
    this.lastServerTouchTime = null;
    this.sessionStartTime = null;
    this.onWarning = null;
    this.onTimeout = null;
    this.onTokenRefreshFailed = null;
    this.onBackgroundRefreshFailed = null;
  }

  // Start session monitoring
  startSession(onWarning, onTimeout, onTokenRefreshFailed, onBackgroundRefreshFailed) {
    // Re-read config so any server overlay applied before start is honored (#628).
    this.idleTimeout = SESSION_CONFIG.IDLE_TIMEOUT;
    this.slidingWindow = SESSION_CONFIG.SLIDING_WINDOW;
    this.warningTime = SESSION_CONFIG.WARNING_TIME;

    this.onWarning = onWarning;
    this.onTimeout = onTimeout;
    this.onTokenRefreshFailed = onTokenRefreshFailed;
    this.onBackgroundRefreshFailed = onBackgroundRefreshFailed;
    this.sessionStartTime = Date.now();
    this.lastActivityTime = Date.now();
    this.lastServerTouchTime = Date.now();

    // Start idle monitoring
    this.resetIdleTimer();
    this.addActivityListeners();

    // Start automatic token refresh
    this.scheduleNextRefresh();

    // Set sliding window timers
    this.setSlidingWindowTimers();
  }

  // Set sliding window warning and timeout
  setSlidingWindowTimers() {
    // Warning before 12-hour limit
    const warningTime = this.slidingWindow - this.warningTime;
    this.slidingWindowWarningTimer = setTimeout(() => {
      if (this.onWarning) {
        this.onWarning({
          type: 'sliding-window-warning',
          secondsRemaining: this.warningTime / 1000,
        });
      }
    }, warningTime);

    // Hard limit at sliding window
    this.slidingWindowTimer = setTimeout(() => {
      this.handleTimeout('sliding-window');
    }, this.slidingWindow);
  }

  // docs/access/slice-3b-plan.md §3.4 — derived from localStorage.expires_at, NOT a fixed interval
  // and NOT a response object: SessionManager never sees one (startSession is called on mount), and
  // login/refresh spell the field differently (expireIn/expiresIn) — reading a response literally
  // would schedule setInterval(fn, NaN), firing every tick. Auth.setSession already normalises both
  // shapes into one absolute expires_at.
  scheduleNextRefresh() {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
    }

    // Guard: a corrupted value must still arm a timer. An exception here would leave startSession
    // with NO timers at all — a silent death at minute 10, worse than a retry loop because nothing
    // reports it.
    let expiresAt = null;
    try {
      expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    } catch (e) {
      expiresAt = null;
    }
    const remaining = Number.isFinite(expiresAt) ? expiresAt - Date.now() : 0;

    // Guard: floor at 30s. A woken laptop has an expires_at in the past, so 80% of a negative
    // number is setTimeout(fn, <=0) — fine once, a tight loop forever if refresh keeps failing
    // against a server that is down.
    //
    // ponytail: the floor can only push the fire past expiry when under ~37.5s of token life
    // remains, and that request is exactly what verifyTokenAllowingExpiry (server §3.1) exists to
    // absorb — expired-but-signed, live sid, inside idle and cap. Do NOT "improve" this to
    // Math.min(delay, remaining): that removes the floor and recreates the tight loop.
    const delay = Math.max(remaining * 0.8, 30000);

    this.tokenRefreshTimer = setTimeout(async () => {
      const success = await this.refreshAuthToken();

      if (success) {
        // Chain continues from the new expires_at.
        this.scheduleNextRefresh();
      } else if (this.onBackgroundRefreshFailed) {
        // Guard: do NOT re-arm on failure. Re-arming is what turns one outage into a retry storm
        // from every open tab; the dialog's own recovery path re-arms on success (extendSession).
        this.onBackgroundRefreshFailed();
      } else {
        this.handleTimeout('token-refresh-failed');
      }
    }, delay);
  }

  // Refresh the authentication token
  async refreshAuthToken() {
    try {
      const currentToken = this.auth.getAccessToken();

      if (!currentToken) {
        console.error('No token found to refresh');
        return false;
      }

      // Call your backend to refresh the token
      const response = await fetch('/auth/refresh-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': currentToken,
        },
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      // Update the token in localStorage
      if (data.accessToken) {
        this.auth.setSession({
          accessToken: data.accessToken,
          // 600, NOT 3600. This default was CORRECT before slice 3b (the token really was an hour)
          // and the TTL change turned it into a 6x lie: absent the field, the client would store a
          // one-hour expiry for a ten-minute token, schedule its refresh at 48 minutes, and be dead
          // at 10 — the precise mass-logout this slice exists to prevent, hidden in a fallback.
          //
          // Defaulting SHORT is safe by construction (it only refreshes more often than needed);
          // defaulting LONG is the outage. So when the server does not say, assume the short one.
          expiresIn: data.expiresIn || 600,
        });

        this.lastServerTouchTime = Date.now();
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }

  // Reset idle timer on user activity
  resetIdleTimer() {
    this.lastActivityTime = Date.now();

    // Clear existing timers
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);

    // Set warning timer (idle timeout - warning time)
    this.warningTimer = setTimeout(() => {
      if (this.onWarning) {
        this.onWarning({
          type: 'idle',
          secondsRemaining: this.warningTime / 1000,
        });
      }
    }, this.idleTimeout - this.warningTime);

    // Set idle timeout
    this.idleTimer = setTimeout(() => {
      this.handleTimeout('idle');
    }, this.idleTimeout);
  }

  // Handle timeout event
  handleTimeout(type) {
    // console.log(`Session timeout: ${type}`);
    this.cleanup();
    if (this.onTimeout) {
      this.onTimeout(type);
    }
  }

  // Add event listeners for user activity
  addActivityListeners() {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
      'input',
      'change',
      'focus',
      'wheel',
    ];
    events.forEach((event) => {
      document.addEventListener(event, this.handleActivity, true);
    });
    // console.log('Session activity listeners added for events:', events);
  }

  // Remove event listeners
  removeActivityListeners() {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
      'input',
      'change',
      'focus',
      'wheel',
    ];
    events.forEach((event) => {
      document.removeEventListener(event, this.handleActivity, true);
    });
  }

  // Handle user activity
  handleActivity = (event) => {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;

    // Only reset timer if it's been more than 500ms since last activity (reduced from 1s for better responsiveness)
    if (timeSinceLastActivity > 500) {
      const timeIdle = Math.floor(timeSinceLastActivity / 1000);
      console.log(`User activity detected (${event.type}), was idle for ${timeIdle}s, resetting timer`);
      this.lastActivityTime = Date.now();
      this.resetIdleTimer();
      this.sendHeartbeat();
    }
  };

  // Best-effort keepalive: keeps the server-side idle window warm while the
  // user is genuinely active. Throttled to SERVER_KEEPALIVE_INTERVAL. A failure
  // is swallowed (never logs the user out) — the idle/warning timers remain the
  // source of truth for session expiry (#606).
  sendHeartbeat = async () => {
    if (this.lastServerTouchTime &&
        (Date.now() - this.lastServerTouchTime) < SESSION_CONFIG.SERVER_KEEPALIVE_INTERVAL) {
      return;
    }
    const prev = this.lastServerTouchTime;
    this.lastServerTouchTime = Date.now(); // stamp eagerly to block concurrent callers
    const token = this.auth.getAccessToken();
    if (!token) {
      this.lastServerTouchTime = prev;
      return;
    }
    try {
      const response = await fetch('/auth/heartbeat', {
        method: 'GET',
        headers: {'x-access-token': token},
      });
      if (!response.ok) {
        throw new Error(`heartbeat ${response.status}`);
      }
    } catch (error) {
      this.lastServerTouchTime = prev; // allow the next activity to retry
      console.warn('Session keepalive failed:', error.message);
    }
  };

  // Extend session (called when user clicks "Stay logged in")
  async extendSession() {
    console.log('Extending session and refreshing token...');

    // Refresh the token
    const success = await this.refreshAuthToken();

    if (success) {
      // Reset idle timer
      this.resetIdleTimer();
      // §3.4 guard 4 — a successful extend RE-ARMS the refresh chain from the new expires_at.
      // Without this, a background refresh failure (which stops the chain, guard 3) plus one
      // successful "Stay Logged In" leaves the chain permanently dead — a silent death at the NEXT
      // expiry, since the dialog's own recovery path does not otherwise go through it.
      this.scheduleNextRefresh();
      return true;
    } else {
      // Token refresh failed
      console.error('Failed to extend session - token refresh failed');
      if (this.onTokenRefreshFailed) {
        this.onTokenRefreshFailed();
      }
      return false;
    }
  }

  // Get session info
  getSessionInfo() {
    const now = Date.now();
    const sessionDuration = now - this.sessionStartTime;
    const timeSinceActivity = now - this.lastActivityTime;
    const slidingWindowRemaining = this.slidingWindow - sessionDuration;

    return {
      sessionDuration,
      timeSinceActivity,
      slidingWindowRemaining,
      sessionStartTime: this.sessionStartTime,
      lastActivityTime: this.lastActivityTime,
      isActive: timeSinceActivity < this.idleTimeout,
    };
  }

  // Cleanup
  cleanup() {
    if (this.idleTimer) clearTimeout(this.idleTimer);
    if (this.warningTimer) clearTimeout(this.warningTimer);
    if (this.tokenRefreshTimer) clearTimeout(this.tokenRefreshTimer);
    if (this.slidingWindowWarningTimer) clearTimeout(this.slidingWindowWarningTimer);
    if (this.slidingWindowTimer) clearTimeout(this.slidingWindowTimer);
    this.removeActivityListeners();
  }

  // Stop session monitoring
  stopSession() {
    this.cleanup();
  }
}

export default SessionManager;
